import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { modalOpen } from './redux/dashboardActions';
import { setUserPermissions } from '../../layout/redux/layoutActions';

import {
  generateBuildingsCompaniesLists,
  payloadBuilder,
} from './realtyOffersPrimary/redux/offersPrimary.controller';
import { generateSecondaryList } from './realtyOffersSecondary/redux/offersSecondary.controller';
import { generateTransactionsList } from './realtyTransactions/redux/realtyTransactions.controller';

import { NameIdPair } from './types';

import SidebarRight from '../../layout/sidebarRight';
import MenuRightSidebar, {
  IMenuItem,
} from './menuRightSideBar/menuRightSideBar';
import MapView from './mapControls/mapView';
import DataLayers from './dataLayers/dataLayers';
import ActionsMap from './mapControls/actionsMap';
import RealtyOffersPrimary from './realtyOffersPrimary/realtyOffersPrimary';
import RealtyOffersPrimaryGeneral from './realtyOffersPrimaryGeneral/realtyOffersPrimary';
import RealtyOffersSecondary from './realtyOffersSecondary/realtyOffersSecondary';
import RealtyOffersSecondaryGeneral from './realtyOffersSecondaryGeneral/realtyOffersSecondary';
import RealtyTransactions from './realtyTransactions/realtyTransactions';
import RealtyTransactionsGeneral from './realtyTransactionsGeneral/realtyTransactions';
import SidebarLeft from '../../layout/sidebarLeft';
import MenuSidebar from './menuSidebar';

import BuildingDetailsModalTransactions from './realtyTransactions/components/buildingDetailsModal/buildingDetailsModal';
import BuildingDetailsModalOffers from './realtyOffersPrimary/components/buildingDetailsModal/buildingDetailsModal';
import BuildingDetailsModalOffersSecondary from './realtyOffersSecondary/components/buildingDetailsModal/buildingDetailsModal';
import BuildingDetailsModalOffersSecondaryGeneral from './realtyOffersSecondaryGeneral/components/buildingDetailsModal/buildingDetailsModal';
import { BuildingDetailsModalSocioeconomicDemographic } from './socioeconomicDemographic/buildingDetailsModal';

import UserOptions, { OptionsUser } from './mapControls/userOptions';
import { BuildingFiltersMenu } from './realtyOffersPrimary/components/buildingFilters/buildingFilters';
import LayersLegends from './mapControls/layersLegends';
import SocioeconomicDemographic from './socioeconomicDemographic';
import SavePolygonsModal from './mapControls/savePolygonsModal';
import Polygons from './polygons';
import DeletePolygonsModal from './mapControls/deletePolygonsModal';
import MarketCycle from './marketCycle/marketCycle';
import { getOwnPolygons, getPolygons } from './polygons/redux/polygonsInfra';
import { Polygon } from './polygons/polygons';
import { AxiosResponse } from 'axios';
import {
  setIsLoadingPolygons,
  updateDrawState,
} from './polygons/redux/polygonsActions';
import { generateMarketCycleList } from './marketCycle/redux/marketCycle.controller';
import { NewStudyModal } from './studies';
import TermsOfUseModal from '../../shared/components/termsOfUse/termsOfUseModal';
import StudyCreationGuide from './studies/components/studyCreationGuide';
import { RenderPDFReport } from './studies/components/pdfReports/render';
import ReportResults from '../reportResults/reportResults';
import { ConfirmExitModal } from './studies/components/confirmExitModal';
import StudiesList from './studies/studiesList';
import DashboardMap from '../dashboardMap/dashboardMap';

import {
  AddAlert,
  IcoDataBase,
  IcoFiltersMap,
  IcoLogOut,
  IconDialogue,
  IconHelp,
  IconPentagon,
  ProductLogo,
} from '../../component';

import './dashboard.scss';
import useActionsOnMapDrag from '../../hooks/useCurrentBounds';
import {
  getFamilyIncomeFromMapDrag,
  isAverageIncomeActive,
} from './dataLayers/controllers/dataLayers.controller';
import { Map } from 'eemovel-map-library';
import { RootState } from '../../reducer';
import { IcoThermomemter } from '../../component/icon/thermometer';
import Heatmap from './heatmap/heatmap';
import { isHeatmapAvailable } from './heatmap/controllers/heatmap.controller';
import { usePoligonOnMap } from '../../shared/hooks/usePoligonOnMap';

const HELP_CENTER_URL = process.env.REACT_APP_HELP_CENTER_URL as string;

const mapViewData: NameIdPair[] = [
  {
    name: 'Mapa padrão',
    id: 'standardMap',
  },
  {
    name: 'Satélite',
    id: 'satellite',
  },
];

export default function Dashboard() {
  const [loadingMapStyle, setLoadingMapStyle] = useState({});
  const [heigthHeader, setHeigthHeader]: any = useState(0);
  const [isOpenDataLayers, setIsOpenDataLayers] = useState<boolean>(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
  const [isHeatMapMenuOpen, setIsHeatMapMenuOpen] = useState<boolean>(false);
  const [buildingsFilterOpen, setBuildingsFilterOpen] =
    useState<boolean>(false);
  const [isPolygonsOpen, setIsPolygonsOpen] = useState<boolean>(false);
  const [dataPolygons, setDataPolygons] = useState<Polygon[]>([]);

  const { preference, user, profileRules } = useSelector(
    (state: RootState) => state.layoutState
  );
  const nls = require(`./nls/${preference.language.id}.json`);

  const { idDashboard }: any = useParams();
  const dispatch = useDispatch();

  const {
    isActive,
    statusModal,
    displayStudyCreationGuide,
    isActiveRightSidebar,
  } = useSelector((state: RootState) => state.dashboardState);
  const { refetch, onlyOwnPolygons } = useSelector(
    (state: RootState) => state.polygonsState
  );

  const { menuAction, mapAction } = useSelector(
    (state: RootState) => state.dashboardState.isActive
  );
  const isStudiesListOpen = menuAction === '6';
  const { isOpen } = useSelector(
    (state: RootState) => state.dataLayersState.dataLayers.userOptions
  );

  const { neighborhood, valueZoning, zoning, averageIncome, flowPoints } =
    useSelector((state: RootState) => state.dataLayersState.dataLayers);

  const { realtyOffersPrimary } = useSelector(
    (state: RootState) => state.offersPrimaryState
  );

  const { bigNumbers } = useSelector(
    (state: RootState) => state.bigNumbersState
  );
  const { isLoadingMap, displayCharts } = useSelector(
    (state: RootState) => state.dashboardState
  );
  const heightApp = { height: `calc(100vh - ${heigthHeader}px)` };

  const { hasAccessGeneralMarkets } = profileRules;

  const { isPoligonOnMap, isCircleOnMap } = usePoligonOnMap();
  const hasPoligon = isPoligonOnMap || isCircleOnMap;
  useEffect(() => {
    if (hasPoligon && (!displayCharts || !Object.keys(bigNumbers).length)) {
      setIsFilterMenuOpen(true);
    } else {
      setIsFilterMenuOpen(false);
    }
  }, [hasPoligon, bigNumbers, displayCharts]);

  useEffect(() => {
    if (mapAction === '5') {
      setIsOpenDataLayers(false);
    }
  }, [mapAction]);

  useEffect(() => {
    const obtainSavedPolygons = async () => {
      try {
        dispatch(setIsLoadingPolygons(true));
        const response = onlyOwnPolygons
          ? ((await getOwnPolygons()) as {
              data: AxiosResponse<Polygon[]>;
            })
          : ((await getPolygons()) as {
              data: AxiosResponse<Polygon[]>;
            });
        setDataPolygons(response?.data?.data);
        dispatch(setIsLoadingPolygons(false));
      } catch {
        dispatch(setIsLoadingPolygons(false));
        setDataPolygons([]);
        dispatch(AddAlert('warning', nls.failGetPolygons));
      }
    };
    obtainSavedPolygons();
  }, [refetch, onlyOwnPolygons]);

  useEffect(() => {
    if (menuAction) {
      if (isOpenDataLayers) {
        setIsOpenDataLayers(false);
      }
      if (isPolygonsOpen) {
        setIsPolygonsOpen(false);
      }
      if (buildingsFilterOpen) {
        setBuildingsFilterOpen(false);
      }

      setIsFilterMenuOpen(true);
    } else {
      setIsFilterMenuOpen(false);
    }

    showDataLayers();
  }, [menuAction]);

  useEffect(() => {
    const boxHeader = document.getElementById('box-header');

    setHeigthHeader(boxHeader?.offsetHeight);
    setIsFilterMenuOpen(false);

    dispatch(setUserPermissions());

    if (menuAction) {
      handleCloseFiltersMenu();
    }

    if (!hasAccessGeneralMarkets) {
      dispatch([
        payloadBuilder(realtyOffersPrimary, null, 'primary', true, true),
        generateBuildingsCompaniesLists(),
        generateSecondaryList(),
        generateTransactionsList(),
        generateMarketCycleList(),
      ]);
    }
  }, []);

  useEffect(() => {
    setIsPolygonsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpenDataLayers) {
      setIsFilterMenuOpen(false);
      setIsPolygonsOpen(false);
      setBuildingsFilterOpen(false);
      setIsHeatMapMenuOpen(false);
    }
  }, [isOpenDataLayers]);

  useEffect(() => {
    if (isPolygonsOpen) {
      setIsFilterMenuOpen(false);
      setIsOpenDataLayers(false);
      setBuildingsFilterOpen(false);
      setIsHeatMapMenuOpen(false);
    }
  }, [isPolygonsOpen]);

  useEffect(() => {
    if (isFilterMenuOpen) {
      setIsOpenDataLayers(false);
      setBuildingsFilterOpen(false);
      setIsPolygonsOpen(false);
      setIsHeatMapMenuOpen(false);
    }
  }, [isFilterMenuOpen]);

  useEffect(() => {
    if (buildingsFilterOpen) {
      setIsFilterMenuOpen(false);
      setIsOpenDataLayers(false);
      setIsPolygonsOpen(false);
      setIsHeatMapMenuOpen(false);
    }
  }, [buildingsFilterOpen]);

  useEffect(() => {
    if (isHeatMapMenuOpen) {
      setIsFilterMenuOpen(false);
      setIsOpenDataLayers(false);
      setIsPolygonsOpen(false);
      setBuildingsFilterOpen(false);
    }
  }, [isHeatMapMenuOpen]);

  useEffect(() => {
    if (isLoadingMap) {
      setLoadingMapStyle({
        cursor: 'wait',
        pointerEvents: 'auto',
      });
    } else {
      setLoadingMapStyle({
        cursor: 'default',
        pointerEvents: 'none',
      });
    }
  }, [isLoadingMap]);

  useEffect(() => {
    if (!Map.map) return;

    const isPoligon = !!Map.PoligonOnMap?.getMap();

    const isCircle = Map.markerCircle?.getMap() && Map.circleRadius !== 0;

    if (isPoligon) {
      const newDrawState = {
        type: 'polygon',
        radius: null,
        coords: Map.PoligonOnMap?.getPath()?.getArray(),
        center: null,
        isActive: true,
      };
      dispatch(updateDrawState(newDrawState));
      return;
    }

    if (isCircle) {
      const newDrawState = {
        type: 'circle',
        radius: Map.circleRadius,
        coords: null,
        center: Map.markerCircle.getCenter(),
        isActive: true,
      };
      dispatch(updateDrawState(newDrawState));
      return;
    }

    if (!isPoligon && !isCircle) {
      const newDrawState = {
        type: 'none',
        radius: null,
        coords: null,
        center: null,
        isActive: false,
      };
      dispatch(updateDrawState(newDrawState));
      return;
    }
  }, [Map.PoligonOnMap, Map.markerCircle, Map.circleRadius]);

  const openNewStudyModal = () => {
    dispatch(modalOpen('new study modal'));
  };

  const hasFlowPointActive = flowPoints.options.some(({ checked }) => checked);
  const rightSidebarMenuOptions: IMenuItem[] = [
    {
      id: '1',
      label: 'Camadas',
      icon: (
        <IcoDataBase
          style={{ color: 'white' }}
          cy="RightMenuLayersIcon"
          isActive={
            neighborhood.selected ||
            valueZoning.selected ||
            zoning.selected ||
            averageIncome.selected ||
            hasFlowPointActive
          }
        />
      ),
      class: 'large',
      disabled: false,
      isVisible: true,
      action: {
        func: setIsOpenDataLayers,
        value: isOpenDataLayers,
      },
      activeState: isOpenDataLayers,
    },
    {
      id: '2',
      label: 'Filtros',
      icon: (
        <IcoFiltersMap cy="RightMenuFiltersIcon" isActive={displayCharts} />
      ),
      class: 'large',
      isVisible: true,
      disabled: !menuAction || displayCharts === 'socioeconomicDemographic',
      action: {
        func: setIsFilterMenuOpen,
        value: isFilterMenuOpen,
      },
      activeState: isFilterMenuOpen,
      disabledAction: () => {
        dispatch(
          AddAlert(
            'info',
            'Utilize uma ferramenta do mapa para desenhar um novo polígono'
          )
        );
      },
    },
    {
      id: '6',
      label: 'Mapa de calor',
      icon: <IcoThermomemter cy="Heatmap" />,
      class: 'large polygons-button-sidebar',
      isVisible: !hasAccessGeneralMarkets,
      disabled: !isHeatmapAvailable(),
      action: {
        func: setIsHeatMapMenuOpen,
        value: isHeatMapMenuOpen,
      },
      activeState: isHeatMapMenuOpen,
      disabledAction: () => {
        dispatch(
          AddAlert('info', 'Realize uma pesquisa para aplicar o mapa de calor')
        );
      },
    },
    // {
    //   id: '3',
    //   label: 'Empreendimentos e incorporadoras',
    //   icon: <BuildingsFilter cy="RightMenuBuildingsFilter" />,
    //   class: 'large',
    //   disabled: menuAction !== '1' || hasAccessGeneralMarkets,
    //   isVisible: menuAction === '1' && !hasAccessGeneralMarkets,
    //   action: {
    //     func: setBuildingsFilterOpen,
    //     value: buildingsFilterOpen,
    //   },
    //   activeState: buildingsFilterOpen,
    // },
    {
      id: '4',
      label: 'Áreas de estudo',
      icon: <IconPentagon cy="MenuSidebar" width="18" height="18" />,
      class: 'large polygons-button-sidebar',
      isVisible: true,
      disabled: false,
      action: {
        func: setIsPolygonsOpen,
        value: isPolygonsOpen,
      },
      activeState: isPolygonsOpen,
    },
    // {
    //   id: '5',
    //   label: 'Novo relatório',
    //   icon: <IcoPaperSheet cy="NewStudy" />,
    //   class: 'large polygons-button-sidebar',
    //   isVisible: !hasAccessGeneralMarkets,
    //   disabled: false,
    //   action: {
    //     func: openNewStudyModal,
    //     value: statusModal === 'new study modal',
    //   },
    //   activeState: statusModal === 'new study modal',
    // },
  ];

  const userOptions: OptionsUser[] = [
    {
      icon: <IcoLogOut cy="UserOptions" />,
      text: nls.optionsUser.logOut,
      separatorAbove: true,
      action: () => {
        dispatch(modalOpen('sair'));
      },
      textClass: 'user-options-logout-button-text',
    },
    {
      icon: <IconDialogue cy="UserOptions" />,
      text: nls.optionsUser.chat,
      action: () => {
        if (window.fcWidget.isOpen()) return window.fcWidget.close();
        return window.fcWidget.open();
      },
      disabled: false,
    },
    {
      icon: <IconHelp cy="UserOptions" />,
      text: nls.optionsUser.help,
      action: () => window.open(HELP_CENTER_URL, '_blank'),
      disabled: false,
    },
  ];

  const handleCloseFiltersMenu = () => {
    setIsFilterMenuOpen(false);
    setBuildingsFilterOpen(false);
  };
  const showDataLayers = () => {
    if (isFilterMenuOpen) {
      switch (menuAction) {
        case '1':
          return (
            <RealtyOffersPrimary closeModalAction={handleCloseFiltersMenu} />
          );
        case '2':
          return (
            <RealtyOffersSecondaryGeneral
              closeModalAction={handleCloseFiltersMenu}
            />
          );
        case '3':
          return (
            <RealtyTransactions closeModalAction={handleCloseFiltersMenu} />
          );
        case '4':
          return (
            <SocioeconomicDemographic
              closeModalAction={handleCloseFiltersMenu}
            />
          );
        case '5':
          return <MarketCycle closeModalAction={handleCloseFiltersMenu} />;

        case '6':
          return <StudiesList />;
          break;
        case '7':
          return (
            <RealtyOffersPrimaryGeneral
              closeModalAction={handleCloseFiltersMenu}
            />
          );
        case '9':
          return (
            <RealtyTransactionsGeneral
              closeModalAction={handleCloseFiltersMenu}
            />
          );
        default:
          return null;
      }
    }
    if (buildingsFilterOpen) {
      return <BuildingFiltersMenu closeModalAction={handleCloseFiltersMenu} />;
    }
  };

  useActionsOnMapDrag(isAverageIncomeActive(), getFamilyIncomeFromMapDrag);

  return (
    <>
      <div
        className={
          isActive.sidebar
            ? 'box-dashboard close-sidebar'
            : 'box-dashboard open-sidebar'
        }
      >
        {idDashboard === 'dashboard' || idDashboard === undefined ? (
          <SidebarLeft style={heightApp}>
            <>
              <div
                className="logo"
                onClick={() => window.open('/map/dashboard', '_self')}
              >
                <ProductLogo cy="ProductLogo" />
              </div>
              <MenuSidebar />
            </>
          </SidebarLeft>
        ) : null}

        {(idDashboard === 'dashboard' || idDashboard === undefined) &&
        !isStudiesListOpen ? (
          <SidebarRight style={heightApp}>
            <>
              <div className="right-bar-options">
                <MenuRightSidebar data={rightSidebarMenuOptions} />
              </div>
            </>
          </SidebarRight>
        ) : null}

        <div className="box-dashboard-container">
          <div className="box-dashboard-container-row">
            <div className="box-dashboard-map">
              <div className="box-map-google">
                <div className="box-loading-map" style={loadingMapStyle} />
                <ActionsMap />
                <DataLayers
                  isOpen={isOpenDataLayers}
                  setIsOpen={setIsOpenDataLayers}
                />
                <Heatmap isOpen={isHeatMapMenuOpen} />
                <MapView data={mapViewData} />
                <LayersLegends />
                <UserOptions
                  options={userOptions}
                  containerClass="user-options-position-internal-container"
                  isVisible={isOpen}
                />
                <Polygons data={dataPolygons} isVisible={isPolygonsOpen} />
                {showDataLayers()}
                <DashboardMap />
              </div>
            </div>
          </div>
          <ReportResults />
        </div>
      </div>
      {statusModal === 'open building' && <BuildingDetailsModalOffers />}
      {statusModal === 'open building transactions' && (
        <BuildingDetailsModalTransactions />
      )}
      {statusModal === 'open building secondary' && (
        <BuildingDetailsModalOffersSecondary />
      )}
      {statusModal === 'open building secondary-general' && (
        <BuildingDetailsModalOffersSecondaryGeneral />
      )}
      {statusModal === 'open building socioeconomic demographic' && (
        <BuildingDetailsModalSocioeconomicDemographic />
      )}
      {statusModal === 'new study modal' && <NewStudyModal />}
      {displayStudyCreationGuide && <StudyCreationGuide />}
      {statusModal === 'render pdf preview' && <RenderPDFReport />}
      {statusModal === 'confirm exit study creation guide' && (
        <ConfirmExitModal />
      )}
      <SavePolygonsModal />
      <DeletePolygonsModal />
      <TermsOfUseModal />
    </>
  );
}
